import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service'; // Added

import { ApiService } from '../../../../../core/services/api.service';
import { CityParkingCustomerAddressPayload } from '../../../../../core/models/customer.model';
import { ApiCustomerManagementService } from '../../../../../core/services/api/customer/api-customer-management.service';

interface Province { id: number; name_th: string; name_en: string; geography_id: number; }
interface Amphure { id: number; name_th: string; name_en: string; province_id: number; }
interface Tambon { id: number; zip_code: number; name_th: string; name_en: string; amphure_id: number; }

@Component({
  selector: 'app-modal-fullltax-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './modal-fullltax-form.component.html',
  styleUrl: './modal-fullltax-form.component.css' // Note: styleUrl (v17+)
})
export class ModalFullltaxFormComponent implements OnInit {
  @Input() customerId: number = 0;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  // --- Address State ---
  // Using CityParkingCustomerAddressPayload to match modal-customer
  taxData: CityParkingCustomerAddressPayload = {
    id: 0, name: '', fullName: '', address: '', subDistrict: '', district: '',
    province: '', zipCode: '', headOffice: true, taxId: '', branchNo: '',
    branchName: '', phone: '', email: '', type: 0, cpCustomerId: 0, userId: 0
  };

  // Master Data (Location)
  tambons: Tambon[] = [];
  amphures: Amphure[] = [];
  provinces: Province[] = [];

  // Filtered Master Data
  filteredAmphures: Amphure[] = [];
  filteredTambons: Tambon[] = [];

  // Address Selection State (IDs)
  selectedProvinceId: number | null = null;
  selectedAmphureId: number | null = null;
  selectedTambonId: number | null = null;

  isLoading = false;
  userId: number = 0;

  // --- Dropdown State ---
  openDropdown: string | null = null;

  constructor(
    private apiCustomerManagementService: ApiCustomerManagementService,
    private toast: ToastService,
    private http: HttpClient,
    private cookieService: CookieService
  ) { }

  ngOnInit(): void {
    this.initializeUserId();
    this.loadMasterData();
    this.setTaxData();

  }

  private initializeUserId() {
    const userIdFromSession = sessionStorage.getItem('user_id');
    const userIdFromCookie = this.cookieService.get('user_id');
    this.userId = userIdFromCookie ? parseInt(userIdFromCookie) : (userIdFromSession ? JSON.parse(userIdFromSession) : 0);

  }

  /* ---------------------- Master Data Loading ---------------------- */

  private loadMasterData() {
    forkJoin({
      tambons: this.http.get<any[]>('json/thai_tambons.json'),
      amphures: this.http.get<any[]>('json/thai_amphures.json'),
      provinces: this.http.get<any[]>('json/thai_provinces.json'),
    }).subscribe({
      next: (response) => {
        this.tambons = response.tambons;
        this.amphures = response.amphures;
        this.provinces = response.provinces as Province[];
        // If we were editing, we would call startEditAddressFilters() here
      },
      error: (err) => console.error('Failed to load location master data', err)
    });
  }

  setTaxData() {
    //console.log("Customer ID", this.customerId, "User ID", this.userId);
    if (this.customerId) this.taxData.cpCustomerId = this.customerId;
    if (this.userId) this.taxData.userId = this.userId;

    //console.log("Default Data", this.taxData);
  }
  /* ---------------------- Location Logic ---------------------- */

  onProvinceChange() {
    this.selectedAmphureId = null;
    this.selectedTambonId = null;
    this.filteredAmphures = [];
    this.filteredTambons = [];
    this.taxData.district = '';
    this.taxData.subDistrict = '';
    this.taxData.zipCode = '';

    if (this.selectedProvinceId) {
      const province = this.provinces.find(p => p.id == this.selectedProvinceId);
      if (province) {
        this.taxData.province = province.name_th;
        this.filteredAmphures = this.amphures.filter(a => a.province_id == this.selectedProvinceId);
      }
    } else {
      this.taxData.province = '';
    }
  }

  onAmphureChange() {
    this.selectedTambonId = null;
    this.filteredTambons = [];
    this.taxData.subDistrict = '';
    this.taxData.zipCode = '';

    if (this.selectedAmphureId) {
      const amphure = this.amphures.find(a => a.id == this.selectedAmphureId);
      if (amphure) {
        this.taxData.district = amphure.name_th;
        this.filteredTambons = this.tambons.filter(t => t.amphure_id == this.selectedAmphureId);
      }
    } else {
      this.taxData.district = '';
    }
  }

  onTambonChange() {
    if (this.selectedTambonId) {
      const tambon = this.tambons.find(t => t.id == this.selectedTambonId);
      if (tambon) {
        this.taxData.subDistrict = tambon.name_th;
        this.taxData.zipCode = String(tambon.zip_code);
      }
    } else {
      this.taxData.subDistrict = '';
      this.taxData.zipCode = '';
    }
  }

  /* ---------------------- Dropdown Getters ---------------------- */

  get provinceOptions() {
    return this.provinces.map(p => ({ label: p.name_th, value: p.id }));
  }

  get amphureOptions() {
    return this.filteredAmphures.map(a => ({ label: a.name_th, value: a.id }));
  }

  get tambonOptions() {
    return this.filteredTambons.map(t => ({ label: t.name_th, value: t.id }));
  }

  /* ---------------------- Dropdown Logic ---------------------- */

  toggleDropdown(dropdown: string, event: Event) {
    event.stopPropagation();
    if (this.openDropdown === dropdown) {
      this.openDropdown = null;
    } else {
      this.openDropdown = dropdown;
    }
  }

  closeDropdowns() {
    this.openDropdown = null;
  }

  selectOption(dropdown: string, value: any) {
    switch (dropdown) {
      case 'provinces':
        this.selectedProvinceId = value;
        this.onProvinceChange();
        break;
      case 'amphures':
        this.selectedAmphureId = value;
        this.onAmphureChange();
        break;
      case 'tambons':
        this.selectedTambonId = value;
        this.onTambonChange();
        break;
    }
    this.closeDropdowns();
  }

  getOptionName(dropdown: string, value: any): string {
    if (value === null || value === undefined) return '';
    switch (dropdown) {
      case 'provinces':
        return this.provinces.find(p => p.id == value)?.name_th || '';
      case 'amphures':
        return this.amphures.find(a => a.id == value)?.name_th || '';
      case 'tambons':
        return this.tambons.find(t => t.id == value)?.name_th || '';
      default:
        return '';
    }
  }

  /* ---------------------- Form Actions ---------------------- */

  closeModalForm() {
    this.close.emit();
  }

  onHqChange() {
    if (this.taxData.headOffice) {
      this.taxData.branchName = ''; // Was 'branch'
      this.taxData.branchNo = '';    // Was 'branch_no' (modal-customer sets empty, checks only if NOT headOffice)
    }
  }

  /* ---------------------- Save / API ---------------------- */

  onSaveForm() {
    // Logic from saveNewAddress
    console.log("Save Form Tax Data", JSON.stringify(this.taxData));
    if (this.prepareAndValidateAddressPayload()) {
      this.isLoading = true;
      this.apiCustomerManagementService.updateCityparkingAddressCustomer(this.taxData)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe({
          next: (res) => {
            if (res.isSuccess) {
              this.toast.success('บันทึกข้อมูลเรียบร้อย');
              this.save.emit();
              this.closeModalForm();
            } else {
              this.toast.error(res.message);
            }
          },
          error: (err) => this.toast.error('เกิดข้อผิดพลาดในการบันทึกที่อยู่')
        });
    }
  }

  private prepareAndValidateAddressPayload(): boolean {
    const data = this.taxData;

    // Auto-set name if empty
    if (!data.name || !data.name.trim()) {
      data.name = data.headOffice ? 'สำนักงานใหญ่' : (data.branchName || 'ที่อยู่จัดส่ง');
    }

    // Validation
    if (!data.fullName) { this.toast.error('กรุณาระบุชื่อผู้รับ/ผู้เสียภาษี'); return false; }
    if (!data.taxId) { this.toast.error('กรุณาระบุเลขประจำตัวผู้เสียภาษี'); return false; }
    if (!data.address) { this.toast.error('กรุณาระบุที่อยู่'); return false; }
    if (!data.subDistrict || !data.district || !data.province) { this.toast.error('กรุณาระบุข้อมูลพื้นที่ให้ครบถ้วน'); return false; }
    if (!data.zipCode || !/^[0-9]{5}$/.test(data.zipCode)) { this.toast.error('รหัสไปรษณีย์ไม่ถูกต้อง'); return false; }

    // Phone is optional in some contexts but modal-customer requires it?
    // User form has it.
    // if (!data.phone) { this.toast.error('กรุณาระบุเบอร์โทรศัพท์'); return false; }

    if (data.type === undefined || data.type === null) {
      this.toast.error('กรุณาระบุประเภท (บุคคลธรรมดา/นิติบุคคล)');
      return false;
    }

    return true;
  }

}
